// app/components/privacy-graph-scrolly/elements/workload-zone.tsx
//
// Single workload-zone rectangle. Wraps the customer-order workload's nodes
// (api-gw, lambda, dynamo, s3, ddb-snap) by default; expands to include
// Bedrock when `includeDrift` is true (drift hits → workload's footprint grew).

import { LAYOUT, ZONE, ZONE_PAD, FEATURED_WORKLOAD_NODES } from "../graph-layout";

const PAD = ZONE_PAD;
const BEDROCK_ID = "bedrock";

function bbox(ids: readonly string[]) {
  const nodes = ids.map((id) => {
    const n = LAYOUT.nodes.find((n) => n.id === id);
    if (!n) throw new Error(`workload-zone: unknown node "${id}"`);
    return n;
  });
  const minX = Math.min(...nodes.map((n) => n.x));
  const minY = Math.min(...nodes.map((n) => n.y));
  const maxX = Math.max(...nodes.map((n) => n.x + n.w));
  const maxY = Math.max(...nodes.map((n) => n.y + n.h));
  return {
    x: minX - PAD,
    y: minY - PAD,
    w: maxX - minX + PAD * 2,
    h: maxY - minY + PAD * 2,
  };
}

const BASE_RECT  = bbox(FEATURED_WORKLOAD_NODES);
const DRIFT_RECT = bbox([...FEATURED_WORKLOAD_NODES, BEDROCK_ID]);

/** Bounding box for the workload zone (without drift). Beats anchor modals + chips off this. */
export const WORKLOAD_ZONE_BBOX = {
  rect: BASE_RECT,
  driftRect: DRIFT_RECT,
  // Backwards-compat shims for code paths still referencing the old notched layout.
  leftRect: BASE_RECT,
  rightRect: BASE_RECT,
  enclosing: BASE_RECT,
};

interface Props {
  label?: string;
  opacity?: number;
  /** When true, the rect grows to include Bedrock (beat 5+). */
  includeDrift?: boolean;
}

const LABEL_Y_OFFSET = 14;

export function WorkloadZone({ label = "", opacity = 1, includeDrift = false }: Props) {
  const rect = includeDrift ? DRIFT_RECT : BASE_RECT;
  const labelY = rect.y - LABEL_Y_OFFSET;

  return (
    <g aria-label="Workload zone" opacity={opacity}>
      <rect
        x={rect.x}
        y={rect.y}
        width={rect.w}
        height={rect.h}
        rx={ZONE.radius}
        ry={ZONE.radius}
        fill={ZONE.fill}
        stroke={ZONE.stroke}
        strokeWidth={ZONE.strokeWidth}
        strokeDasharray={ZONE.dash}
      />
      {label && (
        <text
          x={rect.x + rect.w / 2}
          y={labelY}
          textAnchor="middle"
          fontSize={11}
          letterSpacing="0.08em"
          fill={ZONE.labelColor}
          fontFamily="var(--font-mono, ui-monospace, SFMono-Regular, monospace)"
        >
          {label}
        </text>
      )}
    </g>
  );
}
